import { computed, ref, type ComputedRef, type Ref } from "vue";
import {
  MAX_OTP_ATTEMPTS,
  type EventDetail,
  type EventMeta,
  type GateError,
  type GateState,
  type JoinErrorCode,
  type JoinInlineError,
} from "@/types/gate";

export interface GateMachine {
  state: Ref<GateState>;
  isTerminal: ComputedRef<boolean>;
  submitWhatsapp: (whatsapp: string) => void;
  otpSent: () => void;
  otpSendFailed: (error: GateError) => void;
  changeNumber: () => void;
  submitCode: () => void;
  codeValidated: (eventId: string) => void;
  codeInvalid: () => void;
  resume: (eventId: string) => void;
  eventFetched: (event: EventDetail) => void;
  paywall: (event: EventDetail) => void;
  eventFetchFailed: (error: GateError) => void;
  requestJoin: () => void;
  joinInlineFailed: (error: JoinInlineError) => void;
  joinCapacityRace: () => void;
  joinFailed: (code: JoinErrorCode, event?: EventMeta) => void;
  fail: (error: GateError) => void;
  reset: () => void;
}

export function useGateMachine(): GateMachine {
  const state = ref<GateState>({ kind: "awaiting_whatsapp" });

  const fail = (error: GateError): void => {
    state.value = { kind: "error", error };
  };

  return {
    state,
    isTerminal: computed(() => state.value.kind === "error"),
    submitWhatsapp: (whatsapp) => {
      if (state.value.kind !== "awaiting_whatsapp") return;
      state.value = { kind: "sending_otp", whatsapp };
    },
    otpSent: () => {
      if (state.value.kind !== "sending_otp") return;
      state.value = {
        kind: "awaiting_code",
        whatsapp: state.value.whatsapp,
        attemptsRemaining: MAX_OTP_ATTEMPTS,
      };
    },
    otpSendFailed: (error) => {
      fail(error);
    },
    changeNumber: () => {
      state.value = { kind: "awaiting_whatsapp" };
    },
    submitCode: () => {
      if (state.value.kind !== "awaiting_code") return;
      state.value = {
        kind: "validating_otp",
        whatsapp: state.value.whatsapp,
        attemptsRemaining: state.value.attemptsRemaining,
      };
    },
    codeValidated: (eventId) => {
      if (state.value.kind !== "validating_otp") return;
      state.value = { kind: "fetching_event", eventId };
    },
    codeInvalid: () => {
      if (state.value.kind !== "validating_otp") return;
      const remaining = state.value.attemptsRemaining - 1;
      if (remaining <= 0) {
        fail({ code: "otp_locked" });
        return;
      }
      state.value = {
        kind: "awaiting_code",
        whatsapp: state.value.whatsapp,
        attemptsRemaining: remaining,
      };
    },
    resume: (eventId) => {
      if (state.value.kind !== "awaiting_whatsapp") return;
      state.value = { kind: "fetching_event", eventId };
    },
    eventFetched: (event) => {
      if (state.value.kind !== "fetching_event") return;
      state.value = {
        kind: "awaiting_join",
        eventId: state.value.eventId,
        event,
        inlineError: null,
        submitting: false,
      };
    },
    paywall: (event) => {
      if (state.value.kind !== "fetching_event") return;
      state.value = { kind: "paywall", eventId: state.value.eventId, event };
    },
    eventFetchFailed: (error) => {
      fail(error);
    },
    requestJoin: () => {
      if (state.value.kind !== "awaiting_join") return;
      state.value = {
        ...state.value,
        submitting: true,
        inlineError: null,
      };
    },
    joinInlineFailed: (error) => {
      if (state.value.kind !== "awaiting_join") return;
      state.value = {
        ...state.value,
        submitting: false,
        inlineError: error,
      };
    },
    joinCapacityRace: () => {
      if (state.value.kind !== "awaiting_join") return;
      state.value = { kind: "fetching_event", eventId: state.value.eventId };
    },
    joinFailed: (code, event) => {
      fail({ code, ...(event ? { event } : {}) });
    },
    fail,
    reset: () => {
      state.value = { kind: "awaiting_whatsapp" };
    },
  };
}
